import { useDispatch, useSelector } from 'react-redux'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import React, { useRef, useEffect, useState, useCallback } from 'react'
import { PaymentRequest, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js'
import { RootState } from 'redux/store'
import { setPaymentMethod } from 'redux/features/paymentDetails'
import useFlow from 'modules/flow/state/use-flow'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import PaymentActionButton from './payment-action-button'
import useStripePaymentRequest from 'utils/functions/useStripePaymentRequest'
import { PaymentSelectionButtonProps } from 'modules/flow/components/payment-methods-step/components/payment-selection-button'
import { statesApi } from 'redux/services/states'
import { getClassySessionId } from 'modules/utils/getClassySessionId'
import { PaymentMethod } from 'models/ChargeIntent'
import useFinalizeSingleStepOrder from '../../../../order/finalize-order/use-finalize-single-step-order'
import {
  validateForms,
  saveFormsData,
} from '../../steps/single/utils/single-step-form-orchestrator'
import { trackFormSubmitEvent } from 'modules/analytics/helpers/track-form-events'
import useStatus from 'modules/status/state/use-status'
import { StatusType } from 'modules/status/state/status-types'

import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'
import mParticleSdk from '@mparticle/web-sdk'
import { getCookieValue } from 'modules/utils/getCookieValue'
import { DEFAULT_THEME_COLORS } from '../../../../../css/theme'

const DigitalWalletsButton = ({ color, children, ...restProps }: PaymentSelectionButtonProps) => {
  const canMakePayment = _useCanMakePayment()
  const paymentRequest = _useStripePaymentRequest()
  const flow = useFlow()
  const order = useOrder()
  const config = useConfig()
  const status = useStatus()

  const onClick = useCallback(async () => {
    if (!paymentRequest) return

    trackFormSubmitEvent(flow.currentStep.name)

    try {
      status.set(StatusType.SUBMITTING)

      // Validate all forms and save data before opening digital wallet modal
      if (!config.isEmbedded) {
        const validationResult = await validateForms()

        if (!validationResult.success) {
          throw new Error(validationResult.error || 'Please fill in all required fields')
        }

        saveFormsData(validationResult.results || [], order)
      }

      status.set(StatusType.IDLE)

      // Now show the digital wallet modal
      paymentRequest.show()
    } catch (error) {
      console.error('Error validating custom questions:', error)
      status.set(StatusType.FAILED, 'An error occurred while validating the form')
    }
  }, [paymentRequest, flow, order, config, status])

  return canMakePayment && paymentRequest ? (
    <PaymentActionButton
      type="button"
      color={DEFAULT_THEME_COLORS['--theme-color--neutral-20']}
      onClick={onClick}
      {...restProps}
    >
      {children}
    </PaymentActionButton>
  ) : (
    <></>
  )
}

export default DigitalWalletsButton

const _useCanMakePayment = () => {
  const canMakePayment = useSelector(
    (state: RootState) => state.order.stripePaymentRequest.canMakePayment,
  )

  return canMakePayment
}

const _useStripePaymentRequest = (): PaymentRequest | null => {
  const flow = useFlow()
  const order = useOrder()
  const dispatch = useDispatch()
  const stripePaymentRequestPromise = useStripePaymentRequest()
  const [lazyStates] = statesApi.useLazyGetStatesQuery()

  const trackEvent = useTrackUIElementEvent()
  const finalizeSingleStepOrder = useFinalizeSingleStepOrder()

  const stripePaymentRequestLoadedRef = useRef(false)

  const [stripePaymentRequest, setStripePaymentRequest] = useState<PaymentRequest | null>(null)

  useEffect(() => {
    // Check to make sure it's only loaded once
    if (stripePaymentRequest || stripePaymentRequestLoadedRef.current) {
      return
    }

    stripePaymentRequestLoadedRef.current = true

    const onStripePaymentMethod = async (event: PaymentRequestPaymentMethodEvent) => {
      try {
        const { payerName, paymentMethod } = event

        dispatch(setPaymentMethod(paymentMethod))

        const names = payerName?.split(' ')

        let firstName = ''
        let lastName = ''

        if (names && names.length > 1) {
          firstName = names[0]
          lastName = names[names.length - 1]
        }

        const email = paymentMethod.billing_details?.email ?? ''

        const address = paymentMethod.billing_details.address

        const country = address?.country ?? ''
        let state = address?.state ?? ''

        // test if we actually got a ISO 3166-2 code (Apple Pay known to not do this)
        if (country && state && (state.length > 3 || state.toUpperCase() != state)) {
          const { data: states } = await lazyStates({
            countryCode: country,
          })

          // ultimately if we fail to find the right isoCode, donor will have to fill it out later
          state = states?.find((stateData) => state == stateData.name)?.isoCode ?? ''
        }

        // Set contact info
        order.contact.setValue({ ...order.contact.value, email, lastName, firstName })

        // Set address info
        order.address.setValue({
          city: paymentMethod.billing_details.address?.city ?? '',
          country: paymentMethod.billing_details.address?.country ?? '',
          line1: paymentMethod.billing_details.address?.line1 ?? '',
          zipCode: paymentMethod.billing_details.address?.postal_code ?? '',
          state: paymentMethod.billing_details.address?.state ?? '',
        })

        trackEvent('Donation Payment Details Submitted', mParticleSdk.EventType.Other)

        event.complete('success')

        if (window.newrelic) {
          window.newrelic.addPageAction('payment_method_selected', {
            pgdid: getCookieValue('pgdid'),
            'classy-session-id': getClassySessionId(),
            paymentMethod: PaymentMethod.DW,
          })
        }

        await finalizeSingleStepOrder()
      } catch (e) {
        event.complete('fail')
        Bugsnag.notify(e as NotifiableError)
      }
    }

    const initializePaymentRequest = async () => {
      const pr = await stripePaymentRequestPromise

      if (pr) {
        pr.on('paymentmethod', onStripePaymentMethod)
        setStripePaymentRequest(pr)
      }
    }

    initializePaymentRequest()
  }, [
    flow,
    dispatch,
    order.address,
    order.contact,
    lazyStates,
    finalizeSingleStepOrder,
    stripePaymentRequest,
    stripePaymentRequestPromise,
    trackEvent,
  ])

  return stripePaymentRequest
}
